import Keycloak from "keycloak-js";
import axios from "axios";
import create from "zustand";

import { myLocalStorage } from "../components/StorageHelper";
import useUserStore from "./userStore";
import { setInitialSessionTime } from "./Helpers";

import { decodeKeycloakToken, sendTokenToBackend } from "../utils/tokenHelpers";
import { getClientId } from "./Auth";
import { NGROK } from "../APIs";

let local_kc;

export const useKeycloakStore = create((set) => ({
  local_kc: null,
  setLocalKc: (kcInstance) => set({ local_kc: kcInstance }),
}));

const fetchUserData = async (email) => {
  const { getUserData } = useUserStore.getState();
  await getUserData(email);
};

const extractParamsFromURL = (url) => {
  const urlObj = new URL(url);
  const params = new URLSearchParams(urlObj.search);

  return {
    tenantId: urlObj.hostname.split(".")[0],
    clientId: params.get("client_id"),
    responseType: params.get("response_type"),
    responseMode: params.get("response_mode"),
    scope: params.get("scope"),
  };
};

const exchangeCodeForToken = async (code, email) => {
  const axiosInstance = axios.create();
  try {
    const response = await axiosInstance.post(
      `${NGROK}/api/exchange-azureToken?email=${email}`,
      {
        token: code,
      },
    );
    if (response?.data?.access_token) {
      const accessToken = response.data.access_token;
      const refreshToken = response.data.refresh_token;
      const { exp } = decodeKeycloakToken(accessToken) || {};

      // Persist tokens first so route guards can immediately recognize auth state.
      myLocalStorage.setItem("refresh_token", refreshToken);
      myLocalStorage.setItem("access_token", accessToken);
      myLocalStorage.setItem("email", email);

      if (!myLocalStorage.getItem("role")) {
        myLocalStorage.setItem("role", "TENANT_USER");
      }

      await sendTokenToBackend(
        "",
        email,
        "TENANT_USER",
        accessToken,
        exp,
        "azureSSO",
      );

      await fetchUserData(email);
      return true;
    }
    return false;
  } catch (error) {
    console.error("Error exchanging authorization code for token:", error);
    return false;
  }
};

const initKeycloak = async (realmName) => {
  const axiosInstance = axios.create();
  const response = await axiosInstance.get(`${NGROK}/api/keycloak-url`);
  const keycloakUrl = response.data;
  const clientId = getClientId(realmName);

  local_kc = new Keycloak({
    url: keycloakUrl,
    realm: realmName,
    clientId,
    scope: "openid",
  });

  local_kc.onAuthSuccess = () => {
    const email = local_kc.tokenParsed.email || myLocalStorage.getItem("email");
    const role = myLocalStorage.getItem("role");

    fetchUserData(email);

    const logoutUri = local_kc.createLogoutUrl({
      redirectUri: `${NGROK}`,
    });

    const saveKeycloakInstance = () => {
      useKeycloakStore.getState().setLocalKc(local_kc);
    };

    saveKeycloakInstance();

    setInitialSessionTime(30);

    const { exp } = decodeKeycloakToken(local_kc.token);
    sendTokenToBackend(realmName, email, role, local_kc.token, exp);

    myLocalStorage.setItem("refresh_token", local_kc.refreshToken);
    myLocalStorage.setItem("access_token", local_kc.token);
    myLocalStorage.setItem("email", local_kc.tokenParsed.email);
    myLocalStorage.setItem("logoutUri", logoutUri);

    myLocalStorage.removeItem("keycloakIsInitializing");
  };

  local_kc.onAuthError = () => {
    myLocalStorage.removeItem("keycloakIsInitializing");
  };

  try {
    const authenticated = await local_kc.init({
      onLoad: "check-sso",
      promiseType: "native",
      pkceMethod: "S256",
    });

    if (authenticated) {
      myLocalStorage.removeItem("keycloakIsInitializing");
    } else {
      myLocalStorage.removeItem("keycloakIsInitializing");

      myLocalStorage.setItem("realm", realmName);
    }
  } catch (error) {
    console.error("Keycloak initialization error:", error);
  }
};

const _kc = () => {
  return local_kc;
};

const doLogin = async (email) => {
  try {
    console.log("User is logged in!");

    if (local_kc)
      await local_kc.login({
        loginHint: email,
        redirectUri: `${NGROK}/loading`,
      });
  } catch (error) {
    console.error("Keycloak login error:", error);
  }
};

const doRegister = async (email, firstName, lastName, redirectUri) => {
  console.log(local_kc);
  const isAdminRegistering =
    local_kc &&
    (local_kc.realm === "whiteswan_tenants" ||
      local_kc.realm === "whiteswan_tenants_legacy");

  const targetRedirect = redirectUri || `${NGROK}/loading`;

  try {
    if (isAdminRegistering) {
      await local_kc.login({
        loginHint: email,
        action: "register",
        redirectUri: targetRedirect,
      });
    } else if (local_kc && local_kc.realm !== "whiteswan_tenants") {
      await local_kc.login({
        loginHint: email,
        action: "register",
        redirectUri: targetRedirect,
      });
    }
  } catch (error) {
    console.error("Keycloak login error:", error);
  }
};

const doLogout = () => {
  local_kc.logout();
};
const getToken = () => local_kc?.token;
const isLoggedIn = () => !!local_kc?.token;

const getUsername = () => local_kc.tokenParsed?.preferred_username;
const hasRole = (roles) => roles.some((role) => local_kc.hasRealmRole(role));

const UserService = {
  doLogin,
  initKeycloak,
  doLogout,
  doRegister,
  isLoggedIn,
  getToken,
  getUsername,
  hasRole,
  _kc,
  exchangeCodeForToken,
};
export default UserService;
